import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Download, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { contentAPI, downloadAPI } from '@/utils/api';
import { Loader } from '@/components/ui/loader';

gsap.registerPlugin(ScrollTrigger);

export const Home = () => {
  const { t, i18n } = useTranslation();
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const [stats, setStats] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, servicesRes] = await Promise.all([
        contentAPI.getStats(),
        contentAPI.getServices()
      ]);
      setStats(statsRes.data);
      setServices(servicesRes.data.slice(0, 6));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;

    // Hero Animation
    const heroTimeline = gsap.timeline();
    heroTimeline
      .from('.hero-headline', {
        opacity: 0,
        y: 50,
        duration: 1.2,
        ease: 'power3.out'
      })
      .from('.hero-subheadline', {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: 'power3.out'
      }, '-=0.6')
      .from('.hero-cta', {
        opacity: 0,
        y: 20,
        duration: 0.6,
        stagger: 0.2,
        ease: 'power3.out'
      }, '-=0.4');

    // Stats Counter Animation
    const statsElements = document.querySelectorAll('.stat-value');
    statsElements.forEach((statEl) => {
      const finalText = statEl.textContent.trim();
      // Extract numeric part for animation (handles things like "5+", "₹20 Crore")
      const numeric = parseFloat(finalText.replace(/[^0-9.-]/g, ''));
      if (isNaN(numeric)) {
        // nothing to animate, leave as-is
        return;
      }

      ScrollTrigger.create({
        trigger: statEl,
        start: 'top 80%',
        onEnter: () => {
          gsap.fromTo(
            statEl,
            { textContent: 0 },
            {
              textContent: numeric,
              duration: 2,
              ease: 'power2.out',
              snap: { textContent: 1 },
              onComplete: () => {
                // restore original formatting (plus signs, currency, etc.)
                statEl.textContent = finalText;
              }
            }
          );
        },
        once: true
      });
    });

    // Service Cards Animation
    // clear any previous transform/opacity in case cards are reused
    gsap.set('.service-card', { clearProps: 'transform,opacity' });

    gsap.from('.service-card', {
      scrollTrigger: {
        trigger: '.services-section',
        start: 'top 70%'
      },
      opacity: 0,
      y: 50,
      stagger: 0.15,
      duration: 0.8,
      ease: 'power3.out'
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [loading]);

  const getLangContent = (content) => {
    return content?.[i18n.language] || content?.en || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Loader className="text-secondary" text={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="bg-primary">
      {/* Hero Section */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 28, 45, 0.8), rgba(11, 28, 45, 0.9)), url('https://images.unsplash.com/photo-1769913747940-d73353897587?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBza3lzY3JhcGVyJTIwYmx1ZSUyMGdvbGR8ZW58MHx8fHwxNzcyMjY5ODc4fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center z-10">
          <h1 data-testid="hero-headline" className="hero-headline font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none text-white mb-6">
            {t('hero.headline')}
          </h1>
          <p data-testid="hero-subheadline" className="hero-subheadline text-lg md:text-xl leading-relaxed text-gray-300 max-w-3xl mx-auto mb-12">
            {t('hero.subheadline')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-consultation"
                className="hero-cta bg-secondary text-primary hover:bg-secondary-light px-8 py-6 text-base font-semibold uppercase tracking-wider rounded-sm btn-glow"
              >
                {t('hero.cta_consultation')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <a href={downloadAPI.corporateProfile()} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="cta-download"
                variant="outline"
                className="hero-cta border-2 border-secondary text-secondary hover:bg-secondary hover:text-primary px-8 py-6 text-base font-semibold uppercase tracking-wider rounded-sm"
              >
                <Download className="mr-2" size={20} />
                {t('hero.cta_download')}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Performance Stats Section */}
      <section ref={statsRef} data-testid="stats-section" className="py-24 md:py-32 bg-[#111827]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {stats.map((stat) => (
              <div
                key={stat.key}
                data-testid={`stat-${stat.key}`}
                className="text-center p-8 glass-card card-hover rounded-sm"
              >
                <div className="stat-value text-5xl md:text-6xl font-bold text-secondary font-heading mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-300 text-sm uppercase tracking-widest">
                  {getLangContent(stat.label)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section data-testid="services-overview" className="services-section py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">
              Our Services
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Comprehensive corporate recovery and NPA resolution solutions tailored to your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {services.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                data-testid={`service-card-${service.slug}`}
                className="block h-full"
              >
                <div className="service-card group relative flex flex-col justify-between bg-[#111827] border border-white/5 hover:border-secondary/50 p-8 transition-all duration-500 overflow-hidden card-hover rounded-sm h-full">
                  <div className="relative z-10">
                    <h3 className="font-heading text-2xl font-medium text-white mb-3 group-hover:text-secondary transition-colors">
                      {getLangContent(service.title)}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">
                      {getLangContent(service.description)}
                    </p>
                    <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-2 transition-transform">
                      {t('common.learn_more')}
                      <ChevronRight size={16} className="ml-1" />
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-20">
            <Link to="/services">
              <Button
                data-testid="view-all-services"
                variant="outline"
                className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-primary px-8 py-4 text-sm font-semibold uppercase tracking-wider rounded-sm"
              >
                {t('common.view_all')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-[#111827]">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
            Ready to Resolve Your Corporate Debt?
          </h2>
          <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
            Get in touch with our experts for a confidential consultation and strategic recovery planning.
          </p>
          <Link to="/contact">
            <Button
              data-testid="cta-contact"
              className="bg-secondary text-primary hover:bg-secondary-light px-10 py-6 text-base font-semibold uppercase tracking-wider rounded-sm btn-glow"
            >
              {t('common.contact_us')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
